/**
 * SectionNav's tab model + per-tab colour tokens (data only — no components,
 * so react-refresh stays happy) —
 * split out of `SectionNav.tsx` on 2026-08-30 (CR-034/035) to keep that
 * file under the 250-line cap once the "Download Full Report" pill joined
 * the strip (same pattern as `mobile-menu-drawer-tabs.tsx`).
 */
import { ROUTES } from '../../lib/constants';

/**
 * Per-tab identity. Drives both color tokens and hover-bg opacity rules.
 * Mirrors the `_archive/home.html` per-tab override block at lines 736–754.
 */
export type TabId = 'home' | 'map' | 'rankings' | 'detail' | 'compare' | 'providers';

export interface NavTab {
  readonly id: TabId;
  readonly to: string | null;
  readonly label: string;
  readonly icon: React.ReactNode;
}

export const TABS: ReadonlyArray<NavTab> = [
  {
    id: 'home',
    to: ROUTES.home,
    label: 'Home',
    icon: (
      <>
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
        <polyline points="9 21 9 12 15 12 15 21" />
      </>
    ),
  },
  {
    id: 'map',
    to: ROUTES.map,
    label: 'Map',
    icon: (
      <>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
        <circle cx="12" cy="9" r="2.5" />
      </>
    ),
  },
  {
    id: 'rankings',
    to: ROUTES.rankings,
    label: 'Rankings',
    icon: (
      <>
        <rect x="2" y="10" width="5" height="12" rx="1" />
        <rect x="9.5" y="6" width="5" height="16" rx="1" />
        <rect x="17" y="2" width="5" height="20" rx="1" />
      </>
    ),
  },
  {
    id: 'detail',
    to: ROUTES.detail,
    label: 'Detail',
    icon: (
      <>
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </>
    ),
  },
  {
    id: 'compare',
    to: ROUTES.compare,
    label: 'Compare',
    icon: (
      <>
        <rect x="2" y="3" width="8" height="18" rx="1" />
        <rect x="14" y="3" width="8" height="18" rx="1" />
      </>
    ),
  },
  {
    id: 'providers',
    to: ROUTES.providers,
    label: 'Providers',
    icon: (
      <>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        <line x1="12" y1="12" x2="12" y2="16" />
        <line x1="10" y1="14" x2="14" y2="14" />
      </>
    ),
  },
];

/**
 * Active state class lookup. Per the prototype's per-tab override block
 * (lines 738–754), active state = colored text + colored 3px bottom border
 * + bg-transparent + font-bold. Map is the only tab where text and border
 * are different shades (text = gold-dark, border = gold).
 */
export const ACTIVE_TEXT: Record<TabId, string> = {
  home: 'text-navy',
  map: 'text-gold-dark',
  rankings: 'text-blue',
  detail: 'text-rose',
  compare: 'text-green2',
  // Prototype's home.html declares its own `--teal: #0891B2` (line 34) and
  // uses it for Providers (line 754) — that maps to our `teal-dark` token.
  providers: 'text-teal-dark',
};
export const ACTIVE_BORDER: Record<TabId, string> = {
  home: 'border-b-navy',
  map: 'border-b-gold',
  rankings: 'border-b-blue',
  detail: 'border-b-rose',
  compare: 'border-b-green2',
  providers: 'border-b-teal-dark',
};

/**
 * Hover state class lookup. Each tab gets its own background tint at the
 * exact opacity the prototype uses (.05–.07) and the text color flips to
 * the tab's accent. Inactive-but-hovering icon also bumps opacity to .8.
 */
export const HOVER_CLASS: Record<TabId, string> = {
  home: 'hover:bg-navy/[0.05] hover:text-navy [&:hover_svg]:opacity-80',
  map: 'hover:bg-gold/[0.07] hover:text-gold-dark [&:hover_svg]:opacity-80',
  // A11y (WCAG 1.4.3): hover text sits on the accent's own /0.07 tint, so blue
  // and green2 (both ~4.36:1 there) drop to their AA-safe dark variants. gold
  // uses gold-dark (darkened token) and teal-dark/rose already clear 4.5:1.
  rankings: 'hover:bg-blue/[0.07] hover:text-blue-link [&:hover_svg]:opacity-80',
  detail: 'hover:bg-rose/[0.06] hover:text-rose [&:hover_svg]:opacity-80',
  compare: 'hover:bg-green2/[0.07] hover:text-green-dark [&:hover_svg]:opacity-80',
  providers: 'hover:bg-teal-dark/[0.07] hover:text-teal-dark [&:hover_svg]:opacity-80',
};
